import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PersonChip } from "@/components/MappingPersonRow";
import { UserPicker } from "@/components/UserPicker";
import type { TesterMapping } from "@/hooks/useWizardPersistence";
import type { TesterUser } from "@/services/testers";

interface SubdomainGroupProps {
  subdomainKey: string;
  groupDomain?: string;
  mappingsInGroup: TesterMapping[];
  isExpanded: boolean;
  onToggle: (key: string, open: boolean) => void;
  cognitoUsers: TesterUser[];
  isTestersLoading: boolean;
  isTestersError: boolean;
  onResolve: (mappingId: string, personIndex: number, user: TesterUser) => void;
  onRemove: (mappingId: string, personIndex: number) => void;
  onAddUser: (mappingId: string, user: TesterUser) => void;
  onRetryTesters: () => void;
}

export function SubdomainGroup({
  subdomainKey,
  groupDomain,
  mappingsInGroup,
  isExpanded,
  onToggle,
  cognitoUsers,
  isTestersLoading,
  isTestersError,
  onResolve,
  onRemove,
  onAddUser,
  onRetryTesters,
}: SubdomainGroupProps) {
  const groupResolved = mappingsInGroup.reduce(
    (n, m) => n + m.testPersons.filter(p => p.cognitoUserId).length, 0
  );
  const groupTotal = mappingsInGroup.reduce((n, m) => n + m.testPersons.length, 0);

  return (
    <Collapsible open={isExpanded} onOpenChange={(open) => onToggle(subdomainKey, open)}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
          <ChevronRight className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 shrink-0",
            isExpanded && "rotate-90"
          )} />
          {groupDomain && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
              {groupDomain}
            </Badge>
          )}
          <span className="font-medium flex-1 text-left text-sm truncate">{subdomainKey}</span>
          <span className={cn(
            "text-xs tabular-nums shrink-0",
            groupResolved === groupTotal && groupTotal > 0
              ? "text-success"
              : groupResolved > 0
              ? "text-amber-600"
              : "text-muted-foreground"
          )}>
            {groupResolved}/{groupTotal}
          </span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-5 mt-0.5 space-y-0.5 pb-1">
          {mappingsInGroup.map((mapping) => (
            <div
              key={mapping.id}
              className="flex items-start gap-2 py-1 pl-3 border-l-2 border-muted/60"
            >
              <Badge
                variant="outline"
                className="shrink-0 text-[11px] px-1.5 py-0 mt-1 font-normal text-muted-foreground"
              >
                {mapping.area}
              </Badge>
              <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
                {mapping.testPersons.map((person, pIdx) => (
                  <PersonChip
                    key={`${mapping.id}-${pIdx}`}
                    person={person}
                    mappingId={mapping.id}
                    personIndex={pIdx}
                    cognitoUsers={cognitoUsers}
                    isTestersLoading={isTestersLoading}
                    isTestersError={isTestersError}
                    onResolve={onResolve}
                    onRemove={onRemove}
                    onRetryTesters={onRetryTesters}
                  />
                ))}
                <UserPicker
                  mode="add"
                  onSelect={(u) => onAddUser(mapping.id, u)}
                  excludeUserIds={mapping.testPersons.map(p => p.cognitoUserId).filter(Boolean) as string[]}
                  cognitoUsers={cognitoUsers}
                  isLoading={isTestersLoading}
                  isError={isTestersError}
                  onRetry={onRetryTesters}
                />
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
